import { useState } from 'react';
import { useT } from '../../contexts/LocaleContext';

// Month view of a supplier's tours: one bar per tour from departure to return,
// with a dot on the days they run one of the tour's activities. Clicking a
// tour opens it, like a card on the list view.

const MAX_LANES = 3; // bars per week before "+N more"

const toMF = (d) => (d + 6) % 7; // getDay() → Monday-first index

function parseDate(s) {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function buildWeeks(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const start = new Date(year, month, 1 - toMF(first.getDay()));
  const weeks = [];
  while (start <= last) {
    weeks.push(Array.from({ length: 7 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)));
    start.setDate(start.getDate() + 7);
  }
  return weeks;
}

const STYLE = {
  action: 'bg-[#F59E0B] hover:bg-[#D97706] text-[#451A03]',
  upcoming: 'bg-[#10B981] hover:bg-[#059669] text-white',
  past: 'bg-[#94A3B8] hover:bg-[#64748B] text-white',
};

export default function SupplierCalendar({ tours, onOpen }) {
  const t = useT();
  const today = isoDate(new Date());

  // Tours with dates; a missing return date counts as a one-day tour.
  const items = tours
    .filter((x) => x.tour?.departureDate)
    .map((x) => {
      const startDate = x.tour.departureDate;
      const endDate = x.tour.returnDate && x.tour.returnDate >= startDate ? x.tour.returnDate : startDate;
      const dep = parseDate(startDate);
      const activityDays = new Set((x.myItems || [])
        .filter((i) => Number(i.day) >= 1)
        .map((i) => isoDate(new Date(dep.getFullYear(), dep.getMonth(), dep.getDate() + Number(i.day) - 1))));
      const needsAction = (x.approvals || []).length > 0 || (x.bookings || []).some((b) => b.status === 'pending');
      return {
        requestId: x.requestId,
        label: x.tour.tourName || `${t.value('sport', x.tour.sport) || ''} — ${x.tour.destination || ''}`,
        startDate,
        endDate,
        activityDays,
        kind: endDate < today ? 'past' : needsAction ? 'action' : 'upcoming',
      };
    });

  // Open on the month of the next tour (or this month).
  const [cursor, setCursor] = useState(() => {
    const next = items.filter((i) => i.endDate >= today).sort((a, b) => a.startDate.localeCompare(b.startDate))[0];
    const d = next && next.startDate > today ? parseDate(next.startDate) : new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const { year, month } = cursor;
  const move = (delta) => setCursor(({ year: y, month: m }) => {
    const d = new Date(y, m + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const goToday = () => { const d = new Date(); setCursor({ year: d.getFullYear(), month: d.getMonth() }); };

  const weeks = buildWeeks(year, month);
  const monthLabel = new Date(year, month, 1).toLocaleDateString(t.dateLocale, { month: 'long', year: 'numeric' });
  const dayNames = weeks[0].map((d) => d.toLocaleDateString(t.dateLocale, { weekday: 'short' }));
  const activityDates = new Set(items.flatMap((i) => [...i.activityDays]));

  const inMonth = items
    .filter((i) => i.startDate <= isoDate(new Date(year, month + 1, 0)) && i.endDate >= isoDate(new Date(year, month, 1)))
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-3 sm:p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-[15px] font-bold text-[#111827] capitalize">{monthLabel}</h3>
        <div data-preview-allow className="flex items-center gap-1">
          <button onClick={goToday} className="px-2.5 py-1 text-[12px] font-semibold text-[#374151] border border-[#E5E7EB] rounded-[7px] hover:bg-[#F9FAFB]">
            {t('supplier.calendar.today')}
          </button>
          <button onClick={() => move(-1)} aria-label={t('supplier.calendar.prev')} className="w-7 h-7 flex items-center justify-center border border-[#E5E7EB] rounded-[7px] hover:bg-[#F9FAFB]">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.2" strokeLinecap="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <button onClick={() => move(1)} aria-label={t('supplier.calendar.next')} className="w-7 h-7 flex items-center justify-center border border-[#E5E7EB] rounded-[7px] hover:bg-[#F9FAFB]">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.2" strokeLinecap="round"><path d="M9 18l6-6-6-6" /></svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-[#E5E7EB] pb-1 mb-1">
        {dayNames.map((n, i) => (
          <div key={i} className="text-center text-[10.5px] font-extrabold uppercase tracking-[0.5px] text-[#9CA3AF]">{n}</div>
        ))}
      </div>

      {weeks.map((week) => {
        const ws = week[0];
        const we = week[6];
        const bars = items
          .filter((i) => parseDate(i.startDate) <= we && parseDate(i.endDate) >= ws)
          .map((i) => {
            const dep = parseDate(i.startDate);
            const ret = parseDate(i.endDate);
            return { ...i, startCol: dep < ws ? 0 : toMF(dep.getDay()), endCol: ret > we ? 6 : toMF(ret.getDay()), isStart: dep >= ws, isEnd: ret <= we };
          })
          .sort((a, b) => a.startCol - b.startCol || (b.endCol - b.startCol) - (a.endCol - a.startCol));
        const lanes = [];
        bars.forEach((bar) => {
          const lane = lanes.find((l) => !l.some((b) => b.startCol <= bar.endCol && b.endCol >= bar.startCol));
          if (lane) lane.push(bar); else lanes.push([bar]);
        });
        const shown = lanes.slice(0, MAX_LANES);
        const hiddenByCol = Array(7).fill(0);
        lanes.slice(MAX_LANES).flat().forEach((b) => { for (let c = b.startCol; c <= b.endCol; c++) hiddenByCol[c] += 1; });

        return (
          <div key={isoDate(ws)} className="border-b border-[#F3F4F6] last:border-b-0 pb-1">
            <div className="grid grid-cols-7">
              {week.map((d) => {
                const iso = isoDate(d);
                const other = d.getMonth() !== month;
                return (
                  <div key={iso} className="h-7 flex items-center justify-center gap-[3px]">
                    <span className={`text-[12px] w-6 h-6 flex items-center justify-center rounded-full
                      ${iso === today ? 'bg-navy text-white font-bold' : other ? 'text-[#D1D5DB]' : 'text-[#374151]'}`}>
                      {d.getDate()}
                    </span>
                    {activityDates.has(iso) && <span className="w-[5px] h-[5px] rounded-full bg-pb" title={t('supplier.calendar.activityDay')} />}
                  </div>
                );
              })}
            </div>
            <div className="space-y-[3px] min-h-[21px]">
              {shown.map((lane, li) => (
                <div key={li} className="grid grid-cols-7">
                  {lane.map((bar) => (
                    <button
                      key={bar.requestId}
                      data-preview-nav
                      onClick={() => onOpen(bar.requestId)}
                      title={bar.label}
                      style={{ gridColumnStart: bar.startCol + 1, gridColumnEnd: bar.endCol + 2 }}
                      className={`h-[21px] px-1.5 text-left text-[11px] font-semibold truncate transition ${STYLE[bar.kind]}
                        ${bar.isStart ? 'rounded-l-full ml-0.5' : ''} ${bar.isEnd ? 'rounded-r-full mr-0.5' : ''}`}
                    >
                      {bar.isStart || bar.startCol === 0 ? bar.label : ''}
                    </button>
                  ))}
                </div>
              ))}
              {hiddenByCol.some(Boolean) && (
                <div className="grid grid-cols-7">
                  {hiddenByCol.map((n, c) => (
                    <div key={c} className="text-center text-[10.5px] font-semibold text-[#6B7280]">
                      {n ? t('supplier.calendar.more', { n }) : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[11.5px] text-[#6B7280]">
        <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-full bg-[#10B981]" />{t('supplier.calendar.legendUpcoming')}</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-full bg-[#F59E0B]" />{t('supplier.calendar.legendAction')}</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-full bg-[#94A3B8]" />{t('supplier.calendar.legendPast')}</span>
        <span className="flex items-center gap-1.5"><span className="w-[6px] h-[6px] rounded-full bg-pb" />{t('supplier.calendar.activityDay')}</span>
      </div>

      {/* This month's tours as a list — easier to tap on a phone than the bars. */}
      <div className="mt-4 pt-3 border-t border-[#E5E7EB]">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.7px] text-[#9CA3AF] mb-2">{t('supplier.calendar.thisMonth')}</p>
        {inMonth.length === 0 ? (
          <p className="text-[12.5px] text-[#9CA3AF]">{t('supplier.calendar.none')}</p>
        ) : (
          <div className="space-y-1.5">
            {inMonth.map((i) => (
              <button
                key={i.requestId}
                data-preview-nav
                onClick={() => onOpen(i.requestId)}
                className="w-full flex items-center gap-2.5 text-left px-3 py-2 rounded-[8px] border border-[#E5E7EB] hover:border-[#9CA3AF] transition"
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STYLE[i.kind].split(' ')[0]}`} />
                <span className="flex-1 min-w-0 text-[13px] font-semibold text-[#111827] truncate">{i.label}</span>
                <span className="text-[12px] text-[#6B7280] whitespace-nowrap">
                  {parseDate(i.startDate).toLocaleDateString(t.dateLocale, { day: 'numeric', month: 'short' })}
                  {i.endDate !== i.startDate && ` – ${parseDate(i.endDate).toLocaleDateString(t.dateLocale, { day: 'numeric', month: 'short' })}`}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
